import React, { FC, Fragment, useState } from 'react';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Theme,
    useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DentistDirectoryItem from '../dentist-directory-item/dentist-directory-item.component';
import { User } from 'models/user';
import { Organization } from 'models/organization';
import { useNavigate } from 'react-router-dom';
import ClinicSelectDialog from 'components/shared/clinic-select-dialog/clinic-select-dialog.component';

type DentistDirectoryListProps = {
    filteredDentists: User[];
};
const DentistDirectoryList: FC<DentistDirectoryListProps> = ({ filteredDentists }: DentistDirectoryListProps) => {
    const { t: translate } = useTranslation(['common', 'client-directory', 'users']);
    const navigate = useNavigate();
    const isLargeScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up('xl'));
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedDentist, setSelectedDentist] = useState<User | null>(null);
    const [fromClick, setFromClick] = useState<'row' | 'button' | ''>('');

    const handleDentistNavigation = (dentist: User, clinic: Organization, clickSource: 'row' | 'button') => {
        const clonedDentist = { ...JSON.parse(JSON.stringify(dentist)), clinic };

        if (clickSource === 'button') {
            navigate('/orders/create-lab', { state: { dentist: clonedDentist } });
        } else if (clickSource === 'row') {
            navigate(`/client-directory/dentists/${clonedDentist._id}/dentist-details?clinicId=${clinic._id}`);
        }
    };

    const openDialog = (dentist: User, clickSource: 'row' | 'button') => {
        setSelectedDentist(dentist);
        setFromClick(clickSource);

        const clinics = dentist.clinics || [];
        if (clinics.length === 1) {
            handleDentistNavigation(dentist, clinics[0], clickSource);
            return;
        }

        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setFromClick('');
        setSelectedDentist(null);
    };

    const handleConfirm = (clinicId: string) => {
        if (!selectedDentist) return;

        const clinic = selectedDentist.clinics?.find((clinic) => clinic._id === clinicId);
        if (!clinic) return;

        handleDentistNavigation(selectedDentist, clinic, fromClick as 'row' | 'button');
        closeDialog();
    };

    return (
        <Fragment>
            <TableContainer component={Paper}>
                <Table size={!isLargeScreen ? 'small' : 'medium'}>
                    <TableHead>
                        <TableRow>
                            <TableCell>{translate('common:NAME')}</TableCell>
                            <TableCell>{translate('common:EMAIL')}</TableCell>
                            <TableCell>{translate('common:CLINIC')}</TableCell>
                            <TableCell>{translate('client-directory:DENTIST_TYPE')}</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredDentists?.map((dentist, index) => (
                            <DentistDirectoryItem key={index} dentist={dentist} onOpenDialog={openDialog} />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {dialogOpen && selectedDentist && (
                <ClinicSelectDialog
                    open={dialogOpen}
                    clinics={selectedDentist.clinics as Organization[]}
                    handleClose={closeDialog}
                    handleConfirm={handleConfirm}
                />
            )}
        </Fragment>
    );
};

export default DentistDirectoryList;
